export class EmbossedRectangle {
    /**
     * Draws a rounded rectangle with embossed effect
     * @param ctx The canvas rendering context
     * @param x The x position
     * @param y The y position
     * @param width The width of the rectangle
     * @param height The height of the rectangle
     * @param cornerRadii Object containing radii for each corner
     * @param fill The fill style to use (can be a color string or gradient)
     */
    public static draw(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        cornerRadii: {
            topLeft: number,
            topRight: number,
            bottomRight: number,
            bottomLeft: number
        },
        fill: string | CanvasGradient
    ) {
        ctx.save();
        
        // Create clipping region with rounded corners
        this.roundRect(ctx, x, y, width, height, cornerRadii);
        ctx.clip();

        // Draw background with fill color or gradient
        ctx.fillStyle = fill;
        ctx.fillRect(x, y, width, height);

        // Constants for edge colors
        const lightColor = 'rgba(255, 255, 255, 0.5)';
        const darkColor = 'rgba(0, 0, 0, 0.2)';
        // Only increase opacity for bottom corners to make them match the darker edges
        const darkColorCorner = 'rgba(0, 0, 0, 0.3)';

        // Draw top edge (solid light)
        ctx.strokeStyle = lightColor;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + cornerRadii.topLeft, y + 0.5);
        ctx.lineTo(x + width - cornerRadii.topRight, y + 0.5);
        ctx.stroke();

        // Create right side gradient that includes the top-right corner
        const rightGradient = ctx.createLinearGradient(
            x + width, y,      // Start from top
            x + width, y + height  // To bottom
        );
        rightGradient.addColorStop(0, lightColor);
        rightGradient.addColorStop(1, darkColor);
        
        // Draw right side with gradient (including top-right corner)
        ctx.strokeStyle = rightGradient;
        if (cornerRadii.topRight > 1) {
            ctx.beginPath();
            ctx.arc(x + width - cornerRadii.topRight, y + cornerRadii.topRight, 
                    cornerRadii.topRight, -Math.PI/2, 0);
            ctx.lineTo(x + width - 0.5, y + height - cornerRadii.bottomRight);
            ctx.stroke();
        } else {
            ctx.beginPath();
            ctx.moveTo(x + width - 0.5, y + cornerRadii.topRight);
            ctx.lineTo(x + width - 0.5, y + height - cornerRadii.bottomRight);
            ctx.stroke();
        }

        // Draw bottom edge (solid dark)
        ctx.strokeStyle = darkColor;
        ctx.beginPath();
        ctx.moveTo(x + cornerRadii.bottomLeft, y + height - 0.5);
        ctx.lineTo(x + width - cornerRadii.bottomRight, y + height - 0.5);
        ctx.stroke();

        // Create left side gradient that includes the top-left corner
        const leftGradient = ctx.createLinearGradient(
            x, y,           // Start from top
            x, y + height   // To bottom
        );
        leftGradient.addColorStop(0, lightColor);
        leftGradient.addColorStop(1, darkColor);
        
        // Draw left side with gradient (including top-left corner)
        ctx.strokeStyle = leftGradient;
        if (cornerRadii.topLeft > 1) {
            ctx.beginPath();
            ctx.arc(x + cornerRadii.topLeft, y + cornerRadii.topLeft, 
                    cornerRadii.topLeft, Math.PI, 3*Math.PI/2);
            ctx.moveTo(x + 0.5, y + cornerRadii.topLeft);
            ctx.lineTo(x + 0.5, y + height - cornerRadii.bottomLeft);
            ctx.stroke();
        } else {
            ctx.beginPath();
            ctx.moveTo(x + 0.5, y + cornerRadii.topLeft);
            ctx.lineTo(x + 0.5, y + height - cornerRadii.bottomLeft);
            ctx.stroke();
        }

        // Draw bottom corners with darker color
        if (cornerRadii.bottomRight > 1) {
            ctx.strokeStyle = darkColorCorner;
            ctx.beginPath();
            ctx.arc(x + width - cornerRadii.bottomRight, y + height - cornerRadii.bottomRight, 
                    cornerRadii.bottomRight, 0, Math.PI/2);
            ctx.stroke();
        }

        if (cornerRadii.bottomLeft > 1) {
            ctx.strokeStyle = darkColorCorner;
            ctx.beginPath();
            ctx.arc(x + cornerRadii.bottomLeft, y + height - cornerRadii.bottomLeft, 
                    cornerRadii.bottomLeft, Math.PI/2, Math.PI);
            ctx.stroke();
        }

        ctx.restore();
    }

    /**
     * Helper method to draw rounded rectangles with individual corner radii
     */
    private static roundRect(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        cornerRadii: {
            topLeft: number,
            topRight: number,
            bottomRight: number,
            bottomLeft: number
        }
    ) {
        ctx.beginPath();
        ctx.moveTo(x + cornerRadii.topLeft, y);
        ctx.arcTo(x + width, y, x + width, y + height, cornerRadii.topRight);
        ctx.arcTo(x + width, y + height, x, y + height, cornerRadii.bottomRight);
        ctx.arcTo(x, y + height, x, y, cornerRadii.bottomLeft);
        ctx.arcTo(x, y, x + width, y, cornerRadii.topLeft);
        ctx.closePath();
    }
} 
